import type { View } from '@servicetim/shared';
import { useDeleteView, useViews } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  buttonVariants,
  type ColumnDef,
  DataTable,
  pagedTableProps,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { useSessionClaims } from '@servicetim/ui/lib';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldCatalog } from '../projects/catalog/field-catalog';
import { VIEW_KIND_LABELS, ViewEditorSheet } from './view-editor-sheet';

/** Saved views of the project with the query-builder editor sheet. */
export function ViewsTable({ projectId }: { projectId: string }) {
  const views = useViews({ projectId });
  const remove = useDeleteView();
  const confirm = useConfirm();
  const catalog = useFieldCatalog(projectId);
  const { userId } = useSessionClaims();
  const [editing, setEditing] = useState<View | null | undefined>(undefined);

  const onDelete = async (view: View) => {
    const ok = await confirm({
      title: `Delete view "${view.name}"?`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => remove.mutateAsync(view.id),
    });
    if (ok) toast.success(`View "${view.name}" deleted`);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<View>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'View',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            <span className="font-medium text-fg">{row.original.name}</span>
            {row.original.ownerId === userId ? <Badge variant="outline">Mine</Badge> : null}
          </span>
        ),
      },
      {
        accessorKey: 'kind',
        header: 'Kind',
        size: 110,
        cell: ({ getValue }) => (
          <Badge variant="secondary">{VIEW_KIND_LABELS[getValue<View['kind']>()]}</Badge>
        ),
      },
      {
        accessorKey: 'shared',
        header: 'Shared',
        size: 100,
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="success">Shared</Badge>
          ) : (
            <span className="text-fg-muted">Private</span>
          ),
      },
      {
        accessorKey: 'portalVisible',
        header: 'Portal',
        size: 100,
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="info">Visible</Badge>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: 'query',
        header: 'Query',
        enableSorting: false,
        cell: ({ row }) => {
          const q = row.original.query;
          const parts = [
            q.filter ? 'filter' : null,
            q.sort.length > 0 ? `${q.sort.length} sort` : null,
            q.groupBy ? `grouped by ${q.groupBy.field}` : null,
          ].filter(Boolean);
          return (
            <span className="text-fg-muted text-sm">
              {parts.length > 0 ? parts.join(' · ') : 'everything'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 160,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Link
              to={row.original.kind === 'board' ? '/board' : '/queue'}
              search={{ view: row.original.id }}
              className={buttonVariants({ variant: 'ghost', size: 'icon' })}
              aria-label={`Open ${row.original.name}`}
              title="Open in the queue / board"
            >
              <ExternalLink />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${row.original.name}`}
              onClick={() => setEditing(row.original)}
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${row.original.name}`}
              onClick={() => void onDelete(row.original)}
            >
              <Trash2 />
            </Button>
          </div>
        ),
      },
    ],
    [userId],
  );

  return (
    <>
      <DataTable
        columns={columns}
        getRowId={(v) => v.id}
        {...pagedTableProps(views)}
        caption="Saved views"
        emptyTitle="No views yet"
        emptyDescription="Save a filter, sort and grouping so a queue or board is one click away."
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setEditing(null)}>
              <Plus />
              New view
            </Button>
          </div>
        }
      />
      <ViewEditorSheet
        projectId={projectId}
        view={editing}
        catalog={catalog}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
      />
    </>
  );
}
